import { apiFetch, ApiError, readCSRFToken } from './client';
import { config } from '../config';
import type { CommandIntent } from './commands';

export interface AIChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

export interface AIChatRequest {
  message: string;
  language?: string;
  history?: AIChatTurn[];
  // voice_used is true when the SPA drafted this prompt via the mic +
  // Whisper transcription path. Drives the AI usage log's voice_used
  // column for the weekly improvement report.
  voice_used?: boolean;
  // conversation_id, when set, appends this exchange to an existing
  // ai.conversation row. Omit to create a new conversation.
  conversation_id?: string;
}

export interface AIChatUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens?: number;
  cache_read_tokens?: number;
}

export interface AIChatResponse {
  // conversation_id is the conversation this exchange was saved to.
  conversation_id: string;
  reply: string;
  // Stable tool ids the AI invoked (deduped). Drives the "AI looked at: members,
  // teams" trail under the reply so users see what was consulted.
  tools_used: string[];
  // Pending CommandIntents the AI created via propose_command. The SPA
  // renders each inline with Approve / Reject buttons; approval routes
  // through the existing /commands/{id}/approve endpoint.
  proposed_intents: CommandIntent[];
  // SPA-relative path suffix (appended to /orgs/:slug) when the AI called
  // the navigate tool. Empty/absent = no navigation. Server validates the
  // value against an allowlist; the AI cannot pass arbitrary URLs here.
  navigation?: string;
  usage: AIChatUsage;
}

export async function sendAIChat(
  token: string | null,
  slug: string,
  input: AIChatRequest,
): Promise<AIChatResponse> {
  return apiFetch<AIChatResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/ai/chat`,
    { method: 'POST', body: input, token },
  );
}

// TOOL_DOMAIN maps a tool id to the domain noun the "looked at …" trail shows,
// so the live trail reads "medlemmar, lag, betalningar" instead of raw ids like
// get_coach_dashboard. Users care which area the AI consulted, not which
// function — many tools collapse to one domain, deduped at render. Pure-action
// tools (propose_command, propose_team_schedule, navigate) are intentionally
// absent: they don't "look at" data, and their result already shows in the reply
// (a proposed change / a navigation). A tool missing here is simply skipped in
// the trail, so a newly added tool degrades gracefully until it's mapped.
export const TOOL_DOMAIN: Record<string, string> = {
  // Members
  search_members: 'members',
  get_member_detail: 'members',
  list_deregistrations: 'members',
  list_chat_contacts: 'members',
  // Teams
  list_teams: 'teams',
  list_team_categories: 'teams',
  get_team_category: 'teams',
  list_team_category_templates: 'teams',
  list_category_rule_presets: 'teams',
  get_team_scheduling_rules: 'teams',
  list_team_scheduling_rules: 'teams',
  // Schedule / activities
  list_activities_in_range: 'schedule',
  list_templates: 'schedule',
  get_template: 'schedule',
  preview_apply_template: 'schedule',
  list_activity_callings: 'schedule',
  list_fixtures: 'schedule',
  // Attendance
  get_attendance_grid: 'attendance',
  // Locations
  list_locations: 'locations',
  get_location: 'locations',
  geocode_search: 'locations',
  geocode_resolve: 'locations',
  // Broadcasts / news
  list_broadcasts: 'broadcasts',
  get_broadcast: 'broadcasts',
  list_news: 'broadcasts',
  // Payments (notices, invoices, fees, membership cards)
  preview_notice_run: 'payments',
  get_team_notice_overview: 'payments',
  list_notices: 'payments',
  get_notice: 'payments',
  explain_fee: 'payments',
  list_fee_config: 'payments',
  get_collection_status: 'payments',
  list_invoices: 'payments',
  list_invoice_batches: 'payments',
  get_invoice: 'payments',
  list_invoice_types: 'payments',
  preview_family_invoice: 'payments',
  list_membercard_templates: 'payments',
  get_membercard_template: 'payments',
  // Website
  list_website_variants: 'website',
  get_website_design: 'website',
  list_curated_fonts: 'website',
  list_website_versions: 'website',
  draft_website_page: 'website',
  // Proactive assistant
  list_assistant_suggestions: 'assistant',
  get_assistant_suggestion: 'assistant',
  list_assistant_checks: 'assistant',
  get_getting_started_progress: 'assistant',
  // Coach goals
  list_coach_goals: 'goals',
  get_coach_dashboard: 'goals',
  // Pulse
  list_pulse_campaigns: 'pulse',
  get_pulse_report: 'pulse',
  get_pulse_trend: 'pulse',
  get_pulse_benchmark: 'pulse',
  list_pulse_schedules: 'pulse',
  get_pulse_summary: 'pulse',
  // Signups
  list_team_signups: 'signups',
};

// Live events surfaced by streamAIChat while the answer is generated.
export interface AIChatStreamHandlers {
  // onText fires with each chunk of the answer as the model types it.
  onText: (delta: string) => void;
  // onTool fires once per tool the agent uses — the "what it's doing" trail.
  onTool: (name: string) => void;
}

// streamAIChat POSTs to the SSE endpoint and drives `handlers` with live text +
// tool events, resolving with the authoritative final AIChatResponse from the
// `done` frame (same payload sendAIChat returns). It rejects with an ApiError
// on an `error` frame, a non-OK response, or a body that never completed — so
// the caller can fall back to the non-streaming sendAIChat.
export async function streamAIChat(
  token: string | null,
  slug: string,
  input: AIChatRequest,
  handlers: AIChatStreamHandlers,
): Promise<AIChatResponse> {
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const csrf = readCSRFToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const res = await fetch(
    `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/ai/chat/stream`,
    { method: 'POST', headers, credentials: 'include', body: JSON.stringify(input) },
  );

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    let code = `http_${res.status}`;
    try {
      const j = JSON.parse(text);
      if (j && typeof j.error === 'string') code = j.error;
    } catch {
      // non-JSON error body — keep the http_<status> code
    }
    throw new ApiError(res.status, code, null);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done: AIChatResponse | null = null;
  let errorCode: string | null = null;

  const handleFrame = (raw: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    if (dataLines.length === 0) return;
    let payload: unknown;
    try {
      payload = JSON.parse(dataLines.join('\n'));
    } catch {
      return;
    }
    const p = payload as Record<string, unknown>;
    switch (event) {
      case 'text':
        if (typeof p.delta === 'string') handlers.onText(p.delta);
        break;
      case 'tool':
        if (typeof p.name === 'string') handlers.onTool(p.name);
        break;
      case 'done':
        done = payload as AIChatResponse;
        break;
      case 'error':
        errorCode = typeof p.error === 'string' ? p.error : 'internal';
        break;
    }
  };

  for (;;) {
    const { value, done: streamDone } = await reader.read();
    if (value) {
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      // SSE frames are separated by a blank line.
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        handleFrame(buf.slice(0, idx));
        buf = buf.slice(idx + 2);
      }
    }
    if (streamDone) break;
  }
  if (buf.trim()) handleFrame(buf);

  if (errorCode) throw new ApiError(200, errorCode, null);
  if (!done) throw new ApiError(200, 'stream_incomplete', null);
  return done;
}
